import React, { useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import useAxiosData from "~/shared/use-axios-data";
import axios from "axios";
import useModal from "~/shared/UseModal";
import DataTable from "~/shared/DataTable";
import ManageStaffModal from "./ManageStaffModal";
import MultiSelectInput from "~/shared/form/MultiSelectInput";
import ControlledForm from "~/shared/form/ControlledForm";
import TextInput from "~/shared/form/TextInput";
import FormResponse from "~/shared/form/FormResponse";
import FormButton from "~/shared/form/FormButton";
import AlertBox from "~/shared/AlertBox";

export default function ManageStaff() {
    const [staffMembers, status, setStaffMembers] = useAxiosData(() => axios.get("admin/staff/get"), []);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [programs] = useAxiosData(() => axios.get("programs"), []);
    const { ModalDialog, showConfirm } = useModal();

    async function deleteStaff(staff, context, confirm) {
        if (confirm && !await showConfirm("Delete Staff", "Are you sure you want to permanently delete this staff account?")) {
            return false;
        }

        context.setLoading(true);
        await axios.post("admin/staff/delete", { id: staff._id });
        setStaffMembers(staffMembers => staffMembers.filter(s => s._id !== staff._id));
    }

    async function deactivateStaff(staff, context) {
        if (!await showConfirm("Deactivate Staff", "Are you sure you want to deactivate this staff account?")) {
            return;
        }

        context.setLoading(true);
        await axios.post("admin/staff/deactivate", { id: staff._id });
        staff.accountStatus = "Deactivated";
        setStaffMembers([...staffMembers]);
    }

    async function reactivateStaff(staff, context) {
        if (!await showConfirm("Reactivate Staff", "Are you sure you want to reactivate this staff account?")) {
            return;
        }

        context.setLoading(true);
        await axios.post("admin/staff/activate", { id: staff._id });
        staff.accountStatus = "Active";
        setStaffMembers([...staffMembers]);
    }

    async function activateStaff(staff, context) {
        if (!await showConfirm("Activate Staff", "Are you sure you want to activate this staff account?")) {
            return;
        }

        context.setLoading(true);
        await axios.post("admin/staff/activate", { id: staff._id });
        staff.accountStatus = "Active";
        setStaffMembers([...staffMembers]);
    }

    function closeModal() {
        setShowEditModal(false);
    }

    function openModal(staff) {
        setShowEditModal(true);
        setSelectedStaff(staff);
    }

    function onSuccess({ _id }, __, data) {
        setStaffMembers([...staffMembers, { _id, email: data.inviteRecipient, accountStatus: "Invite Sent" }]);
    }

    function getStaffActions(staff) {
        if (staff.accountStatus === "Invite Sent") {
            return <DataTable.Action onClick={(_, context) => deleteStaff(staff, context)} manualSpinner>Delete Invite</DataTable.Action>;
        }

        return <>
            <DataTable.Action onClick={() => openModal(staff)}>View</DataTable.Action>
            {staff.accountStatus === "Active" && (
                <DataTable.Action onClick={(_, context) => deactivateStaff(staff, context)} manualSpinner>Deactivate</DataTable.Action>
            )}
            {staff.accountStatus === "Deactivated" && (
                <DataTable.Action onClick={(_, context) => reactivateStaff(staff, context)} manualSpinner>Reactivate</DataTable.Action>
            )}
            {staff.accountStatus === "Onboarding Pending" && (
                <DataTable.Action onClick={(_, context) => activateStaff(staff, context)} manualSpinner>Activate</DataTable.Action>
            )}
            <DataTable.Action onClick={(_, context) => deleteStaff(staff, context, true)} manualSpinner>Delete</DataTable.Action>
        </>;
    }

    function onSubmit(data) {
        data.role = "STAFF";
        data.programs = data.programs.map(name => programs.find(p => p.name === name)._id);
    }

    return (
        <section>
            {ModalDialog}

            <DataTable data={staffMembers} paginate loading={status === "loading"}>
                <DataTable.Column sortId="name" value={({ details }) => details ? `${details.firstName} ${details.lastName}` : "N/A"} sortable sortBy={v => v.details ? `${v.details.firstName} ${v.details.lastName}` : "N/A"}>Name</DataTable.Column>
                <DataTable.Column id="email" sortable>Email</DataTable.Column>
                <DataTable.Column id="accountStatus" flex format={(s, { certifications }) => <>{s} {certifications?.some(c => c.status === "Submitted") && <i className="bi bi-circle-fill fs-6 ngf-red" />}</>} sortable>Status</DataTable.Column>
                <DataTable.Column value={getStaffActions}>Actions</DataTable.Column>
                <DataTable.Message condition={status === "error"}>
                    <AlertBox type="error">An unknown error occurred. Please try again later.</AlertBox>
                </DataTable.Message>
                <DataTable.Message condition={staffMembers.length === 0}>
                    <AlertBox type="info">No staff accounts found.</AlertBox>
                </DataTable.Message>
            </DataTable>

            <button className="large-button mt-3" onClick={() => setShowInviteModal(true)} disabled={status !== "success"}>Invite Staff</button>
            <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Invite Staff</Modal.Title>
                </Modal.Header>
                <Modal.Body className="max-w-lg">
                    <p>
                        To invite a new staff member, enter their email address. They will receive an email invite with instructions on how to set up their account.
                    </p>
                    <ControlledForm onSubmit={onSubmit} post="admin/invite-user" onSuccess={onSuccess} success="Successfully sent invitation email." resetOnSuccess="inviteRecipient">
                        <div className="flex flex-col gap-3">
                            <TextInput label="Email Address" id="inviteRecipient" preset="email" required />
                            <MultiSelectInput label="Programs" id="programs" invalid="At least one program must be selected." placeholder="Select programs..." options={programs.map(p => p.name)} required />
                        </div>
                        <FormResponse />
                        <FormButton legacy submit className="mt-4">Send Invite</FormButton>
                    </ControlledForm>
                </Modal.Body>
            </Modal>

            <ManageStaffModal staff={selectedStaff} show={showEditModal} onHide={closeModal} />
        </section>
    );
}
